import { EditAlertRadiusFormFields } from 'core/config/form-fields';
import { useEffect, useState } from 'react';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import { useDialog } from 'core/providers/DialogProvider';
import { useFlags } from '@atlaskit/flag';
import { ILogisticsMetaDao, ILogisticsMetaDto } from 'core/api/types/meta.interface';
import { MetaApiService } from 'core/api';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useAuthState } from 'core/providers/AuthProvider';
import { IButton } from 'shared/components/buttons/button-interface';
import { ISharedFormField } from 'shared/components/form/form-interface';

export const AddEditLogisticsMetaDialog = ({ logisticsMeta }: { logisticsMeta?: ILogisticsMetaDao | null }) => {
  const { userData } = useAuthState();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const dialog = useDialog();
  const flags = useFlags();
  const [formFields, setFormFields] = useState<ISharedFormField[]>(EditAlertRadiusFormFields);

  const getDefaultValue = (logisticsMeta: ILogisticsMetaDao, key: string) => {
    switch (key) {
      default:
        return logisticsMeta[key as keyof ILogisticsMetaDao];
    }
  };

  const AddEditLogisticsMeta = async (data: ILogisticsMetaDto) => {
    setFormSubmitting(true);

    const payload: ILogisticsMetaDto = {
      uid: 'logistics',
      alertRadius: data.alertRadius,
    };

    try {
      if (logisticsMeta) {
        await MetaApiService.update(payload.uid, payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      } else {
        await MetaApiService.set(
          {
            uid: 'logistics',
            alertRadius: data.alertRadius,
          },
          {
            fullName: userData?.fullName ?? '',
            uid: userData?.uid ?? '',
          }
        );
      }

      showSuccessFlag(
        logisticsMeta ? 'Logistics meta updated' : 'Logistics meta created',
        `The logistics meta has been ${logisticsMeta ? 'updated' : 'created'} successfully`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag(
        logisticsMeta ? 'Logistics meta update failed' : 'Logistics meta creation failed',
        `Unable to ${logisticsMeta ? 'update' : 'create'} a logistics meta, please try again.`,
        flags
      );
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  useEffect(() => {
    const fields = EditAlertRadiusFormFields.map((field) => ({
      ...field,
      defaultValue: logisticsMeta ? getDefaultValue(logisticsMeta, field.key) : field.defaultValue,
    }));

    setFormFields(fields as ISharedFormField[]);
  }, [logisticsMeta]);

  const customContent = () => {
    return (
      <SharedForm
        className='p-4 overflow-y-auto'
        fields={formFields as ISharedFormField[]}
        onSubmit={AddEditLogisticsMeta}
        buttonLabel='Submit'
        loading={formSubmitting}
        cancelButton={cancelButton}
      />
    );
  };

  return <SharedDialogBase title='Edit alert radius' customContentTemplate={customContent()} showButtons={false} />;
};
